import React, { useEffect, useState } from "react";
import { Modal, ModalDialog, Button, Select, Option, Input, Radio, RadioGroup, CircularProgress } from "@mui/joy";
import { getFetch, postFetch } from "../../../../../../fetch/fetch";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./phoneinbound.module.css";

const toastOptions : any = {
    position: "bottom-right",
    autoClose: 3000,
    hideProgressBar: true,
    theme: "colored"
};

const errorText = (message : string) => {
    switch (message) {
        case "noExtension": return "교환기에 없는 내선입니다";
        case "alreadyRegistered": return "이미 등록된 내선입니다";
        case "wrongQueue": return "지점 큐를 선택해주세요";
        case "교환기 내선 조회 실패": return "교환기 내선 조회에 실패했습니다";
        default: return "교환기 반영 실패";
    }
};

//수신자 추가 모달: 내선 선택 → 표시 이름(cidname 자동) → 본사팀/지점 폰 → 추가 (시작: 수신 꺼짐·우선순위 3)
const AddReceiverModal : React.FC<any> = (props) => {

    const [extensions, setExtensions] = useState<any[]>([]);
    const [loadingExtensions, setLoadingExtensions] = useState<boolean>(false);
    const [extension, setExtension] = useState<string>("");
    const [name, setName] = useState<string>("");
    const [receiverType, setReceiverType] = useState<string>("hq");
    const [branchQueue, setBranchQueue] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [loadError, setLoadError] = useState<string>("");

    useEffect(() => {
        if (!props.open) {
            return;
        }
        setExtension("");
        setName("");
        setReceiverType("hq");
        setBranchQueue("");
        setLoadError("");
        loadExtensions();
    }, [props.open, props.groupId]);

    const loadExtensions = async () => {
        if (!props.groupId) {
            return;
        }
        setLoadingExtensions(true);
        try {
            const result : any = await getFetch(`/dashboard/callcenter/extensions?groupId=${props.groupId}`);
            if (result.message === "success") {
                setExtensions(result.extensions || []);
            } else {
                setExtensions([]);
                setLoadError(errorText(result.message));
            }
        } catch (e) {
            console.log(e);
            setExtensions([]);
            setLoadError("교환기 내선 조회에 실패했습니다");
        }
        setLoadingExtensions(false);
    };

    const selectExtension = (value : string) => {
        setExtension(value);
        const found = extensions.find((x : any) => x.extension === value);
        if (found) {
            setName(found.cidname || value);
        }
    };

    const submit = async () => {
        if (!extension) {
            toast.error("내선을 선택해주세요", toastOptions);
            return;
        }
        if (receiverType === "branch" && !branchQueue) {
            toast.error("지점 큐를 선택해주세요", toastOptions);
            return;
        }
        setSubmitting(true);
        try {
            const result : any = await postFetch("/dashboard/callcenter/receivers/add", {
                groupId: props.groupId,
                extension,
                name: name.trim(),
                receiverType,
                branchQueue: receiverType === "branch" ? branchQueue : undefined
            });
            if (result.message === "success") {
                toast.success("수신자가 추가되었습니다 (수신 꺼짐 · 우선순위 3)", toastOptions);
                props.onAdded();
                props.onClose();
            } else {
                toast.error(errorText(result.message), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("교환기 반영 실패", toastOptions);
        }
        setSubmitting(false);
    };

    return (
        <Modal open={props.open} onClose={props.onClose}>
            <ModalDialog>
                <div style={{ width: "360px" }}>
                    <div className={styles.modalTitle}>수신자 추가</div>

                    <div className={styles.modalLabel}>내선</div>
                    {
                        loadingExtensions ?
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#777" }}>
                                <CircularProgress size="sm" /> 교환기 내선 목록 불러오는 중
                            </div>
                            :
                            <Select
                                placeholder={extensions.length === 0 ? "추가할 수 있는 내선이 없습니다" : "내선 선택"}
                                value={extension || null}
                                onChange={(e : any, v : any) => { if (v !== null) selectExtension(v); }}
                                disabled={extensions.length === 0}
                            >
                                {
                                    extensions.map((x : any) => (
                                        <Option key={x.extension} value={x.extension}>{x.extension} — {x.cidname || "(이름 없음)"}</Option>
                                    ))
                                }
                            </Select>
                    }
                    {
                        loadError ?
                            <div className={styles.modalError}>{loadError}</div>
                            :
                            ""
                    }

                    <div className={styles.modalLabel}>표시 이름</div>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="이름" slotProps={{ input: { maxLength: 50 } }} />

                    <div className={styles.modalLabel}>구분</div>
                    <RadioGroup value={receiverType} onChange={(e : any) => setReceiverType(e.target.value)}>
                        <Radio value="hq" label="본사팀 — 전 지점 수신" />
                        <Radio value="branch" label="지점 폰 — 한 지점만" />
                    </RadioGroup>

                    {
                        receiverType === "branch" ?
                            <div>
                                <div className={styles.modalLabel}>지점(큐)</div>
                                <Select
                                    placeholder="지점 큐 선택"
                                    value={branchQueue || null}
                                    onChange={(e : any, v : any) => { if (v !== null) setBranchQueue(v); }}
                                >
                                    {
                                        (props.queues || []).map((q : any) => (
                                            <Option key={q.queue} value={q.queue}>{q.name}{q.name !== q.queue ? ` (${q.queue})` : ""}</Option>
                                        ))
                                    }
                                </Select>
                            </div>
                            :
                            ""
                    }

                    <div className={styles.modalHint}>시작 상태: 수신 꺼짐 · 우선순위 3</div>

                    <div className={styles.modalButtons}>
                        <Button variant="plain" color="neutral" onClick={props.onClose} disabled={submitting}>취소</Button>
                        <Button variant="solid" onClick={submit} loading={submitting} disabled={loadingExtensions || !extension}>추가</Button>
                    </div>
                </div>
            </ModalDialog>
        </Modal>
    );
};

export default AddReceiverModal;
